<template>
  <shop-data>
    <ShopArticle v-if="article">
      <ArticleHeader :icon="article.iconUrl">
        {{ article.name }}
      </ArticleHeader>
      <ArticleDescription v-html="article.description"> </ArticleDescription>
      <ArticlePrice :cadence="article.articleType === 2 ? '' : t('shop.month')">
        {{ roundPrice(article.vatPrice) }} €
      </ArticlePrice>
      <ArticleDates>
        <template v-if="booking.bookedOn && booking.bookedOn > 0">
          <dt>{{ t('shop.bookedOn') }}</dt>
          <dd>
            <time>{{ formatDate(booking.bookedOn) }}</time>
          </dd>
        </template>
        <template v-if="booking.validTo || booking.validFrom">
          <dt>
            {{ t('shop.validLabel') }}
            <span vif="booking.validFrom && !booking.validTo">{{ t('shop.validFromPrefix') }}</span>:
          </dt>
          <dd>
            <time>
              <span v-if="booking.validFrom">
                {{ formatDate(booking.validFrom) }}
              </span>
              <span v-if="booking.validTo">
                -
                {{ formatDate(booking.validTo) }}
              </span>
            </time>
          </dd>
        </template>
        <template v-if="!cancelDate && article.directCancellation">
          <dt>{{ t('shop.cancellationNotice') }}</dt>
          <dd>
            <time>{{ formatCancellationDate(article.cancellationNotice) }}</time>
          </dd>
        </template>
        <template v-if="cancelDate">
          <dt>{{ t('shop.cancelledOn') }}</dt>
          <dd>
            <time>{{ formatDate(booking.validTo) }}</time>
          </dd>
        </template>
      </ArticleDates>
      <ContainedArticles :containedArticles="article.containedArticles" />
      <template v-if="articleCanBeCancelled">
        <Alert v-if="!userCanCancelArticle">
          {{ t('shop.cancelAlertPrefix') }}
          <router-link to="/efriends">
            {{ t('shop.cancelAlertLinkText', { '@count': energyLinesAlertDifference, '@unit': energyLinesAlertDifference > 1 ? 'Energylines' : 'Energyline' }) }}
            </router-link
          >{{ t('shop.cancelAlertSuffix') }}
        </Alert>
        <button v-else class="article__cancel" @click="onCancelClick">
          {{ t('shop.cancelPackage') }}
        </button>
        <CancelArticlePopup :booking="booking" />
      </template>
    </ShopArticle>
  </shop-data>
</template>

<script setup>
  import { computed } from 'vue'
  import { useStrings } from '@/composables/useStrings.ts'
  import ContainedArticles from '@/components/shop/ContainedArticles.vue'
  import ArticleHeader from '@/components/shop/ArticleHeader.vue'
  import ArticleDates from '@/components/shop/ArticleDates.vue'
  import ArticleDescription from '@/components/shop/ArticleDescription.vue'
  import ShopArticle from '@/components/shop/ShopArticle.vue'
  import ArticlePrice from '@/components/shop/ArticlePrice.vue'
  import Alert from '@/components/Alert.vue'
  import ShopData from '@/components/shop/ShopData.vue'
  import { useRoute } from 'vue-router'
  import { useShopStore } from '@/stores/useShopStore'
  import { useCubeStore } from '@/stores/useCubeStore'
  import CancelArticlePopup from '@/components/popups/CancelArticlePopup.vue'
  import { useUnits } from '@/composables/useUnits.ts'
  import { useDate } from '@/composables/useDate.ts'
  import { useSpecificPopup } from '@/composables/useSpecificPopup.ts'

  const { t } = useStrings()
  const shopStore = useShopStore()
  const cubeStore = useCubeStore()
  const currentRoute = useRoute()
  const { roundPrice } = useUnits()
  const { formatDate, formatCancellationDate } = useDate()

  const cancelArticlePopup = useSpecificPopup('cancelArticle')

  const id = Number(currentRoute.params.id)
  const booking = computed(() => shopStore.getBooking(id))
  const article = computed(() => booking?.value?.article)

  const articleCanBeCancelled = computed(() => {
    // TODO Using the same logic as in app-v2. Not sure if this is always correct.
    return !cancelDate.value && article.value.directCancellation
  })
  const cancelDate = computed(() => booking?.value.validTo)

  const energyLinesIncluded = computed(() => {
    return shopStore.getArticleEnergyLines(article.value.articleId)
  })
  const unusedEnergyLines = computed(() => cubeStore.unusedEnergyLines)
  const userCanCancelArticle = computed(() => {
    return unusedEnergyLines.value >= energyLinesIncluded.value
  })
  const energyLinesAlertDifference = computed(() => {
    return energyLinesIncluded.value - unusedEnergyLines.value
  })

  const onCancelClick = () => {
    if (userCanCancelArticle.value) {
      cancelArticlePopup.show()
    }
  }
</script>
<style lang="scss" scoped>
.article__cancel {
  padding: 0;
  margin-bottom: $spacer20;
  appearance: none;
  background: none;
  border: none;
  text-decoration: underline;
  color: currentColor;
}
</style>
